// GMAIL-P1 (build order increment 3 A; integrations design §2.2, §2.4, §2.11): Gmail's push door,
// a mailbox's sync switch, and a company's own OAuth app. Behind the server switch
// `COS_GMAIL_P1` (default off: every route answers 404).
//
//   POST {co}/integrations/gmail/push         Pub/Sub's push of a mailbox change (no principal;
//                                             the push subscription's `token` in the URL), turned
//                                             into a `google_channel` notice for the mailbox
//   GET  {co}/connections/{id}/mail-sync      whether the connection's mailbox is synced
//   PUT  {co}/connections/{id}/mail-sync      turn it on (watch + pull) or off (paused, purged)
//   GET  {co}/oauth-clients/{provider}        the company's own OAuth app (never a secret)
//   PUT  {co}/oauth-clients/{provider}        set or replace it (secrets sealed on receipt)
//   DELETE {co}/oauth-clients/{provider}      remove it (back to the server's app)
import { z } from "zod";
import { NotifyReceipt } from "../intake-rows";
import { LIMITS, Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const core = { feature: "core", tag: "connections" } as const;

export const GmailPushQuery = z.object({ token: z.string().min(1).max(500).optional() });

export const MailSync = z
  .object({
    connection_id: Uuid,
    subscription_id: Uuid,
    /** The mailbox's address (the connection's Google account). */
    mailbox: z.string().max(320),
    state: z.enum(["live", "renewing", "lapsed", "paused"]),
    /** True when Gmail pushes changes (a Pub/Sub topic is set up); false: polled. */
    push: z.boolean(),
    last_pull_at: Timestamp.nullable(),
    expires_at: Timestamp.nullable(),
  })
  .meta({ id: "MailSync" });
export type MailSync = z.infer<typeof MailSync>;

export const MailSyncPut = z.strictObject({ on: z.boolean() }).meta({ id: "MailSyncPut" });

export const OwnClientParams = CompanyParams.extend({ provider: z.enum(["google"]) });

const PubsubTopic = z
  .string()
  .max(300)
  .regex(
    /^projects\/[a-z][a-z0-9-]{4,28}[a-z0-9]\/topics\/[A-Za-z][A-Za-z0-9._~%+-]{2,254}$/,
    "projects/{project}/topics/{topic}",
  );

export const CompanyOauthClient = z
  .object({
    provider: z.enum(["google"]),
    client_id: z.string().max(300),
    pubsub_topic: z.string().max(300).nullable(),
    /** Whether the Pub/Sub push token is set (the token itself is never returned). */
    push_token_set: z.boolean(),
    updated_at: Timestamp,
  })
  .meta({ id: "CompanyOauthClient" });

export const CompanyOauthClientPut = z
  .strictObject({
    client_id: z.string().min(1).max(300),
    client_secret: z.string().min(8).max(LIMITS.name),
    pubsub_topic: PubsubTopic.nullable().optional(),
    /** The token the topic's push subscription carries in its URL (`?token=`). */
    push_token: z.string().min(16).max(500).nullable().optional(),
  })
  .meta({ id: "CompanyOauthClientPut" });

export const mailRoutes = {
  "integrations.gmailPush": defineRoute({
    ...core,
    tag: "intake",
    id: "integrations.gmailPush",
    method: "post",
    path: `${co}/integrations/gmail/push`,
    summary: "Gmail's change notice via Pub/Sub push (verified by the push token)",
    description:
      "Called by Google Pub/Sub, never by a person. The `token` must be the company's push " +
      "token (its own Google app's, else the server's) or the answer is 401 like any bad " +
      "signature. The envelope's mailbox and history id become a `google_channel` notice for " +
      "the mailbox's subscription (channel = subscription, resource = mailbox, message number " +
      "= Pub/Sub `messageId`), with the notify receiver's rules after it: 202 `accepted` (one " +
      "`inbound_event`, one queued pull), `duplicate`, or `ignored` for a mailbox nobody syncs.",
    access: "hook_signature",
    params: CompanyParams,
    query: GmailPushQuery,
    body: z.unknown(),
    response: { status: 202, schema: NotifyReceipt },
    errors: ["hook_paused"],
    emits: ["inbound_event.received"],
  }),
  "connections.mailSync.get": defineRoute({
    ...core,
    id: "connections.mailSync.get",
    method: "get",
    path: `${co}/connections/{id}/mail-sync`,
    summary: "Whether a Google connection's mailbox is synced into the 30-day header index",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: MailSync },
    errors: [],
    emits: [],
  }),
  "connections.mailSync.put": defineRoute({
    ...core,
    id: "connections.mailSync.put",
    method: "put",
    path: `${co}/connections/{id}/mail-sync`,
    summary: "Turn a mailbox's sync on (watch and pull) or off (paused, its index purged)",
    description:
      "Who manages the connection may switch it (a person connection: its owner). On needs " +
      "Gmail read on the connection; the first pull lists the last 30 days, later pulls follow " +
      "Gmail's history, and the header rows (never bodies) are matched to contacts. Off pauses " +
      "the subscription and deletes the connection's header rows at once.",
    access: "member",
    params: CompanyItemParams,
    body: MailSyncPut,
    idempotency: "optional",
    response: { status: 200, schema: MailSync },
    errors: ["connection_unusable"],
    emits: ["tool_connection.mail_sync_changed"],
  }),
  "oauthClients.get": defineRoute({
    ...core,
    id: "oauthClients.get",
    method: "get",
    path: `${co}/oauth-clients/{provider}`,
    summary: "The company's own OAuth app for a provider (the secret is never returned)",
    access: "manager",
    params: OwnClientParams,
    response: { status: 200, schema: CompanyOauthClient },
    errors: [],
    emits: [],
  }),
  "oauthClients.put": defineRoute({
    ...core,
    id: "oauthClients.put",
    method: "put",
    path: `${co}/oauth-clients/{provider}`,
    summary:
      "Use the company's own OAuth app (a Google Workspace internal app) for its connections",
    description:
      "Sealed on receipt; from then on authorize, exchange, refresh and revoke of the company's " +
      "connections to the provider use it, and Gmail read is offered without the server's " +
      "founder gate (an internal app needs no Google review). Existing connections keep tokens " +
      "of the previous app and ask to be signed in again on their next refresh.",
    access: "manager",
    params: OwnClientParams,
    body: CompanyOauthClientPut,
    idempotency: "optional",
    response: { status: 200, schema: CompanyOauthClient },
    errors: [],
    emits: ["oauth_client.changed"],
  }),
  "oauthClients.delete": defineRoute({
    ...core,
    id: "oauthClients.delete",
    method: "delete",
    path: `${co}/oauth-clients/{provider}`,
    summary: "Stop using the company's own OAuth app (back to the server's)",
    access: "manager",
    params: OwnClientParams,
    response: { status: 204 },
    errors: [],
    emits: ["oauth_client.changed"],
  }),
} as const;
